import { normalizeBusinessName, trigramSimilarity } from './text';

export type MatchReason = 'phone' | 'whatsapp' | 'email' | 'business_name' | 'vat_id';

export interface DuplicateProbe {
  phoneE164?: string | null;
  whatsappE164?: string | null;
  email?: string | null;
  businessName?: string | null;
  vatId?: string | null;
}

export interface DuplicateCandidateInput {
  organizationId: string;
  organizationName: string;
  vatId?: string | null;
  phones: string[]; // E.164 of org contacts (phone + whatsapp)
  emails: string[]; // lowercase
}

export interface ScoredCandidate {
  organizationId: string;
  organizationName: string;
  score: number; // 0..100
  reasons: MatchReason[];
  nameSimilarity: number;
  strength: 'strong' | 'weak';
}

/** Name similarity at or above this, alone, suggests a possible duplicate (needs human review). */
export const WEAK_NAME_THRESHOLD = 0.45;
/** Name similarity at or above this counts as the same name. */
export const STRONG_NAME_THRESHOLD = 0.85;

/**
 * Transparent duplicate scoring.
 *  - Exact phone / WhatsApp / email / VAT id → strong match (same person or business).
 *  - Business name similarity alone → weak match ("חשד לכפילות"), even when identical,
 *    because different branches or businesses can share a name.
 */
export function scoreCandidate(probe: DuplicateProbe, c: DuplicateCandidateInput): ScoredCandidate | null {
  const reasons: MatchReason[] = [];
  let score = 0;
  const phones = new Set(c.phones.filter(Boolean));
  if (probe.phoneE164 && phones.has(probe.phoneE164)) {
    reasons.push('phone');
    score += 60;
  }
  if (probe.whatsappE164 && probe.whatsappE164 !== probe.phoneE164 && phones.has(probe.whatsappE164)) {
    reasons.push('whatsapp');
    score += 55;
  }
  if (probe.email && c.emails.includes(probe.email.toLowerCase())) {
    reasons.push('email');
    score += 55;
  }
  if (probe.vatId && c.vatId && probe.vatId.replace(/\D/g, '') === c.vatId.replace(/\D/g, '')) {
    reasons.push('vat_id');
    score += 70;
  }
  const a = normalizeBusinessName(probe.businessName);
  const b = normalizeBusinessName(c.organizationName);
  const nameSimilarity = a && b ? (a === b ? 1 : trigramSimilarity(a, b)) : 0;
  if (nameSimilarity >= WEAK_NAME_THRESHOLD) {
    reasons.push('business_name');
    score += Math.round(nameSimilarity * (nameSimilarity >= STRONG_NAME_THRESHOLD ? 40 : 25));
  }
  if (reasons.length === 0) return null;
  const hasIdentityMatch = reasons.some((r) => r !== 'business_name');
  return {
    organizationId: c.organizationId,
    organizationName: c.organizationName,
    score: Math.min(100, score),
    reasons,
    nameSimilarity: Math.round(nameSimilarity * 100) / 100,
    strength: hasIdentityMatch ? 'strong' : 'weak',
  };
}

export interface DuplicateDecision {
  kind: 'none' | 'strong' | 'weak';
  /** Several existing records share this phone/email/VAT id: the person is already in the CRM, but which record
   *  to attach to needs a human. (A weak decision that is NOT ambiguous is a name-only resemblance.) */
  ambiguous: boolean;
  best: ScoredCandidate | null;
  candidates: ScoredCandidate[];
}

/**
 * Decide what intake should do.
 * Exactly one strong candidate → attach automatically.
 * Several strong candidates (e.g. same phone on two organizations) → ambiguous, send to human review.
 * Name resemblance only → weak: intake still creates the lead (flagged), it never holds it back.
 */
export function decideDuplicate(scored: ScoredCandidate[]): DuplicateDecision {
  const candidates = [...scored].sort((x, y) => y.score - x.score);
  const strong = candidates.filter((c) => c.strength === 'strong');
  if (strong.length === 1) return { kind: 'strong', ambiguous: false, best: strong[0]!, candidates };
  if (strong.length > 1) return { kind: 'weak', ambiguous: true, best: strong[0]!, candidates };
  if (candidates.length > 0) return { kind: 'weak', ambiguous: false, best: candidates[0] ?? null, candidates };
  return { kind: 'none', ambiguous: false, best: null, candidates };
}

export const MATCH_REASON_LABELS: Record<MatchReason, string> = {
  phone: 'טלפון זהה',
  whatsapp: 'WhatsApp זהה',
  email: 'אימייל זהה',
  business_name: 'שם עסק דומה',
  vat_id: 'ח.פ. זהה',
};
